import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

export type BindOnceBeatOptions = {
  trigger: HTMLElement;
  timeline: {
    play: () => unknown;
  };
  start?: string;
};

/**
 * Play a paused timeline once when the trigger crosses `start`.
 * Does not reverse or replay.
 */
export function bindOnceBeat({
  trigger,
  timeline,
  start = 'top 10%',
}: BindOnceBeatOptions): () => void {
  const st = ScrollTrigger.create({
    trigger,
    start,
    once: true,
    onEnter: () => {
      timeline.play();
    },
  });

  if (st.isActive || st.progress > 0) timeline.play();

  return () => st.kill();
}

export const TEXT_REVEAL_FULL = {
  y: 24,
  duration: 0.7,
} as const;

export const TEXT_REVEAL_SUBTLE = {
  y: 12,
  duration: 0.5,
} as const;

export type OnceTextRevealOptions = {
  y?: number;
  duration?: number;
  delay?: number;
  stagger?: number;
  trigger?: HTMLElement;
  start?: string;
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function revealTargets(el: HTMLElement | HTMLElement[]): HTMLElement[] {
  return (Array.isArray(el) ? el : [el]).filter(
    (node): node is HTMLElement => Boolean(node),
  );
}

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

/** `top 85%` → 0.85. Unknown values keep the default used by the fade-up. */
function startLineRatio(start: string): number {
  const match = start.trim().match(/^top\s+(\d+(?:\.\d+)?)%$/i);
  if (!match) return 0.85;
  return Number(match[1]) / 100;
}

function playOnceTextReveal(
  targets: HTMLElement[],
  opts: {
    y: number;
    duration: number;
    delay: number;
    stagger: number;
  },
): () => void {
  gsap.set(targets, { opacity: 0, y: opts.y });
  gsap.to(targets, {
    opacity: 1,
    y: 0,
    duration: opts.duration,
    delay: opts.delay,
    stagger: opts.stagger,
    ease: 'power2.out',
    overwrite: true,
    clearProps: 'opacity,transform',
  });
  return () => gsap.killTweensOf(targets);
}

/**
 * One-time fade-up as the element enters view while scrolling down.
 * Transform + opacity only (no CSS filter). Does not reverse or replay.
 * No-ops when the user prefers reduced motion.
 */
export function bindOnceTextReveal(
  el: HTMLElement | HTMLElement[],
  opts?: OnceTextRevealOptions,
): () => void {
  const targets = revealTargets(el);
  if (!targets.length) return () => undefined;
  if (reducedMotion()) return () => undefined;

  const {
    y = TEXT_REVEAL_FULL.y,
    duration = TEXT_REVEAL_FULL.duration,
    delay = 0,
    stagger = 0,
    trigger = targets[0],
    start = 'top 85%',
  } = opts ?? {};

  gsap.set(targets, { opacity: 0, y });

  const play = () => {
    gsap.to(targets, {
      opacity: 1,
      y: 0,
      duration,
      delay,
      stagger,
      ease: 'power2.out',
    });
  };

  const st = ScrollTrigger.create({
    trigger,
    start,
    once: true,
    onEnter: play,
  });

  if (st.isActive || st.progress > 0) play();

  return () => st.kill();
}

/**
 * Same fade as `bindOnceTextReveal`, but the play is driven by whether the
 * trigger is on screen, not by a cached ScrollTrigger start line.
 *
 * Use this on pages whose layout can grow after the first paint (images, a
 * later pin). A one-time ScrollTrigger can miss that shift and leave the copy
 * at opacity 0; IntersectionObserver still fires when the block is visible.
 */
export function bindOnceTextRevealOnView(
  el: HTMLElement | HTMLElement[],
  opts?: OnceTextRevealOptions,
): () => void {
  const targets = revealTargets(el);
  if (!targets.length) return () => undefined;
  if (reducedMotion()) return () => undefined;
  if (typeof IntersectionObserver === 'undefined') {
    return playOnceTextReveal(targets, {
      y: opts?.y ?? TEXT_REVEAL_FULL.y,
      duration: opts?.duration ?? TEXT_REVEAL_FULL.duration,
      delay: opts?.delay ?? 0,
      stagger: opts?.stagger ?? 0,
    });
  }

  const {
    y = TEXT_REVEAL_FULL.y,
    duration = TEXT_REVEAL_FULL.duration,
    delay = 0,
    stagger = 0,
    trigger = targets[0],
    start = 'top 85%',
  } = opts ?? {};

  const line = startLineRatio(start);
  const inset = Math.max(0, (1 - line) * 100);

  gsap.set(targets, { opacity: 0, y });

  let played = false;
  const play = () => {
    if (played) return;
    played = true;
    gsap.to(targets, {
      opacity: 1,
      y: 0,
      duration,
      delay,
      stagger,
      ease: 'power2.out',
      overwrite: true,
      clearProps: 'opacity,transform',
    });
  };

  const rect = trigger.getBoundingClientRect();
  if (rect.top < window.innerHeight * line && rect.bottom > 0) {
    play();
    return () => gsap.killTweensOf(targets);
  }

  const io = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      play();
      io.disconnect();
    },
    { rootMargin: `0px 0px -${inset}% 0px`, threshold: 0 },
  );
  io.observe(trigger);

  return () => {
    io.disconnect();
    gsap.killTweensOf(targets);
  };
}
